import { UseStore } from 'zustand';

import { FormApi } from './form';

/* 
    Normalization of feature implementation, events, and state management.

    Goals and Conventions:

    Limit mutation, assignment and requests to one location.
    Avoid excessive React property marshalling by providing the equivalent of React contexts or Angular services.
    Avoid business logic that has no relationship to any particular framework from being defined next to vendor markup.
*/

interface Configuration {}

export type Actions = {
  [key: string]: Function;
};

export type Forms = {
  [key: string]: FormApi | FormApi[];
};

type DataRecord = Record<string | number | symbol, unknown>;
type Events<T, U = any> = {
  [K in keyof T]?: (args?: U) => () => void;
};
export type Subscription<T extends DataRecord> = Events<T> & {
  unsubscribe(): void;
};

export type Api<
  Conf extends Configuration = any,
  Data extends DataRecord = any,
  A extends Actions = any,
  F extends Forms = any,
  S extends Subscription<Data | DataRecord> = any
> = {
  configuration?: Conf; // any dependencies necessary for the api to exist and configure its components, ie. request enpoints, layout options, etc.
  data?: UseStore<Data>; // should only be properties that cause re-render, ie. form derived changes, request results, etc.
  actions?: A; // functions that update data
  forms?: F; // api for binding to form components
  events?: S; // listen to changes in data
};

export function subscription<T extends DataRecord>(
  data: T | Array<keyof T>,
  store: UseStore<T>
): Subscription<T> {
  const unsubscribers = [];
  const record: any = {};
  const keys = Array.isArray(data) ? data : Object.keys(data);

  keys.forEach((key) => {
    record[key] = (fn: (args?: any) => void) => {
      unsubscribers.push(
        store.subscribe(
          (value) => {
            fn(value);
          },
          (state) => state[key]
        )
      );
      return unsubscribers[unsubscribers.length - 1];
    };
  });

  record.unsubscribe = () => {
    unsubscribers.forEach((fn) => {
      fn();
    });
  };
  return record as Subscription<T>;
}
